import React, { useMemo, useState } from "react";
import { ArrowRightIcon, CheckIcon, RotateCcwIcon } from "lucide-react";
type Answers = {
  coverage: string;
  property: string;
  priority: string;
};
const questions = [{
  key: "coverage",
  title: "How much of your home?",
  options: ["One room", "A few rooms", "Most of the home", "Whole home"]
}, {
  key: "property",
  title: "What describes your property?",
  options: ["Existing home", "Renovation", "New build", "Replacing an old system"]
}, {
  key: "priority",
  title: "What matters most?",
  options: ["Lowest upfront cost", "Whole-home comfort", "Energy efficiency", "Individual room control", "Minimal visible equipment", "I’m not sure"]
}] as const;
export function DecisionAssistant() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({
    coverage: "",
    property: "",
    priority: ""
  });
  const recommendation = useMemo(() => {
    if (answers.coverage === "One room" || answers.priority === "Lowest upfront cost") return "Rinnai split system";
    if (answers.coverage === "A few rooms" || answers.priority === "Individual room control") return "Rinnai multi-split";
    return "Rinnai ducted air conditioning";
  }, [answers]);
  const current = questions[Math.min(step, questions.length - 1)];
  const currentValue = answers[current.key];
  function select(value: string) {
    setAnswers(previous => ({
      ...previous,
      [current.key]: value
    }));
  }
  function reset() {
    setStep(0);
    setAnswers({
      coverage: "",
      property: "",
      priority: ""
    });
  }
  return <section className="bg-ink py-8 text-white lg:py-12" aria-labelledby="assistant-title" data-id="element-1" data-mp="336erqhs54has">
      <div className="mx-auto grid max-w-[1320px] gap-12 px-5 lg:grid-cols-[0.75fr_1.25fr] lg:px-10" data-id="element-2" data-mp="37zhjckdcxgoc">
        <div className="max-w-lg" data-id="element-3" data-mp="3bzrqi0cvyd4s">
          <p className="text-sm font-bold text-redLight" data-id="element-4" data-mp="3gzntbw9ujyir">A SIMPLE STARTING POINT</p>
          <h2 id="assistant-title" className="mt-4 text-4xl font-bold tracking-[-0.04em] sm:text-5xl" data-id="element-5" data-mp="3gxtb4ppymkid">Not sure what you need?</h2>
          <p className="mt-5 text-lg leading-8 text-white/85" data-id="element-6" data-mp="1lvy9ymcg9vyi">Answer three simple questions and we’ll point you toward the most likely Rinnai solution for your home.</p>
          <p className="mt-6 border-l-2 border-rinnai pl-4 text-sm leading-6 text-white/75" data-id="element-7" data-mp="1gvjzyyragbxu">This guide doesn’t replace professional heat-load calculations. It helps us begin a more useful conversation.</p>
        </div>

        <div className="rounded-xl bg-white p-6 text-ink sm:p-8" data-id="element-8" data-mp="24mpftdx8b485">
          {step < questions.length ? <>
              <div className="flex items-center justify-between gap-4" data-id="element-9" data-mp="20kc9ananr2ab">
                <p className="text-sm font-bold text-rinnai" data-id="element-10" data-mp="2ekwrbh8r0u2r">QUESTION {step + 1} OF 3</p>
                <div className="flex gap-1.5" aria-hidden="true" data-id="element-11" data-mp="29m2auly97hlt">
                  {questions.map((question, index) => <span key={question.key} className={`h-1.5 w-10 rounded-full ${index <= step ? "bg-rinnai" : "bg-line"}`} data-id="element-12" data-mp="k744yn5etadc" data-mpi={index} />)}
                </div>
              </div>
              <h3 className="mt-6 text-2xl font-bold tracking-tight" data-id="element-13" data-mp="f9vrgzv7huif">{current.title}</h3>
              <div className="mt-5 grid gap-2 sm:grid-cols-2" data-id="element-14" data-mp="2qvwe5pbhnwng">
                {current.options.map(option => <button key={option} type="button" onClick={() => select(option)} className={`flex min-h-14 items-center justify-between rounded-md border px-4 py-3 text-left text-sm font-semibold transition-colors duration-150 ease-out ${currentValue === option ? "border-rinnai bg-redTint text-rinnai" : "border-line hover:border-ink"}`} data-id="element-15" data-mp="2vwk407nze7g3">
                    {option}
                    {currentValue === option && <CheckIcon className="h-4 w-4" data-id="element-16" data-mp="2d1wvn04r9rlt" />}
                  </button>)}
              </div>
              <button type="button" disabled={!currentValue} onClick={() => setStep(value => value + 1)} className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-md bg-rinnai font-bold text-white transition-colors duration-150 ease-out hover:bg-rinnaiDark disabled:cursor-not-allowed disabled:bg-line disabled:text-muted sm:w-auto sm:px-8" data-id="element-17" data-mp="235tu9whq9wzx">
                {step === 2 ? "SEE MY STARTING POINT" : "NEXT QUESTION"} <ArrowRightIcon className="h-4 w-4" data-id="element-18" data-mp="1tfgpvw3t3muy" />
              </button>
            </> : <div role="status" data-id="element-19" data-mp="1og431bcxqfsd">
              <p className="text-sm font-bold text-rinnai" data-id="element-20" data-mp="1ofc5huzs27zg">YOUR LIKELY STARTING POINT</p>
              <h3 className="mt-4 text-3xl font-bold tracking-tight" data-id="element-21" data-mp="1jh0vnk9hfxh8">{recommendation}</h3>
              <p className="mt-4 max-w-xl leading-7 text-muted" data-id="element-22" data-mp="1ejr6m5q09ntx">Based on your answers, this is likely to be the best format to explore first. We’ll still assess your home, usage and budget before recommending a specific system.</p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row" data-id="element-23" data-mp="15ob8hci7wmij">
                <a href="#quote" className="flex h-12 items-center justify-center rounded-md bg-rinnai px-6 text-sm font-bold text-white hover:bg-rinnaiDark" data-id="element-24" data-mp="10sm50y32ulgy">GET A QUOTE FOR MY HOME</a>
                <button type="button" onClick={reset} className="flex h-12 items-center justify-center gap-2 rounded-md border border-line px-5 text-sm font-bold" data-id="element-25" data-mp="qsva4valu4gc"><RotateCcwIcon className="h-4 w-4" data-id="element-26" data-mp="grartp529a0g" /> Start again</button>
              </div>
            </div>}
        </div>
      </div>
    </section>;
}